/**
 * Copyright 2022-2025 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { Dispatch } from "react";
import { Switch, Text } from "@nordcloud/gnui";
import { CloudResourcesFilterByRangeFields } from "~/generated/graphql";
import { Mode } from "~/components";
import { FilterWrapper } from "~/components/SidebarWithMultiSelect/FilterWrapper";
import { SelectableListWithSearch } from "~/components/SidebarWithMultiSelect/SelectableListWithSearch";
import { useQueryState } from "~/hooks";
import { isNotEmpty, isNotNil } from "~/tools";
import { ResourceQuery } from "../../types";
import { State } from "./types";

type Props = {
  state: State;
  updateState: Dispatch<Partial<State>>;
};

export function AllocationFilters(props: Props) {
  const { state, updateState } = props;

  const { queryState, updateQueryState } = useQueryState<ResourceQuery>();
  const showUnallocated =
    queryState.allocation ===
    CloudResourcesFilterByRangeFields.Unassigned.toLowerCase();

  const clearApplicationsAndEnvironments = () => {
    updateState({
      applicationsAndEnvironments: [],
      excludeApplicationsAndEnvironments: false,
    });
  };

  return (
    <>
      <Text size="sm" weight="bold">
        Allocation
      </Text>
      <FilterWrapper
        name="Applications and Environments"
        isSelected={
          isNotNil(state.applicationsAndEnvironments) &&
          isNotEmpty(state.applicationsAndEnvironments)
        }
        selectedOptions={state.applicationsAndEnvironments ?? []}
        onClear={clearApplicationsAndEnvironments}
      >
        <SelectableListWithSearch
          allowModeChange
          selectedOptions={state.applicationsAndEnvironments ?? []}
          selectableItems={[]}
          isExcludeSelected={state.excludeApplicationsAndEnvironments}
          onMultiSelect={(selections) => {
            updateState({
              applicationsAndEnvironments: selections,
            });
          }}
          onModeChange={(mode) => {
            updateState({
              excludeApplicationsAndEnvironments: mode === Mode.EXCLUDES,
            });
          }}
        />
      </FilterWrapper>
      <FilterWrapper
        name="Unallocated Estate Records"
        isSelected={showUnallocated}
        selectedOptions={showUnallocated ? ["unassigned"] : []}
        onClear={() => {
          updateQueryState({
            allocation: undefined,
            page: "1",
          });
        }}
      >
        <div style={{ padding: "1rem" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              marginLeft: "0.5rem",
            }}
          >
            <Switch
              checked={showUnallocated}
              onChange={(e) => {
                updateQueryState({
                  allocation: e.target.checked ? "unassigned" : undefined,
                  page: "1",
                });
              }}
              aria-label="Toggle unallocated estate records"
            />
            <span>Show Unallocated Estate Records</span>
          </div>
        </div>
      </FilterWrapper>
    </>
  );
}
